import * as React from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import StickyFooter from './components/StickyFooter';
import { companyName } from '../defaults/AppDefaults';
import backgroundImg from '../images/pexels-gromakova-92378617-9146943.jpg';

const faqs = [
  {
    question: 'How do I book an appointment?',
    answer:
      'Search for a barber or shop from the home page, choose a service and time slot, then confirm your booking. You will receive a confirmation once it is complete.',
  },
  {
    question: 'How do I cancel or reschedule a booking?',
    answer:
      'Sign in to your account and go to your reservations to cancel or reschedule an upcoming appointment.',
  },
  {
    question: 'How do I contact my barber or shop directly?',
    answer:
      'Shop and barber contact details are shown on their profile page once you have made a booking with them.',
  },
  {
    question: 'What payment methods are accepted?',
    answer:
      'We accept all major debit and credit cards through our secure checkout.',
  },
];

const infoCardSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 1.5,
  height: '100%',
  p: 4,
  borderRadius: 2,
  border: '1px solid rgba(255,255,255,0.1)',
  bgcolor: '#16171c',
};

export default function HelpSupport() {
  return (
    <div style={{ backgroundColor: '#0f1013' }}>
      {/* Hero Header */}
      <Box
        sx={{
          backgroundImage: `linear-gradient(rgba(8,9,12,0.82), rgba(8,9,12,0.9)), url(${backgroundImg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          textAlign: 'center',
          py: 10,
          px: 2,
          mt: 6,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            Help & Support
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              maxWidth: 700,
              mx: 'auto',
            }}
          >
            We&apos;re here to help. Reach out to us or browse our frequently
            asked questions below.
          </Typography>
        </Container>
      </Box>

      {/* Content */}
      <Container
        maxWidth="md"
        sx={{
          py: 8,
          px: { xs: 3, sm: 4 },
        }}
      >
        {/* Contact options */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={4}>
            <Box sx={infoCardSx}>
              <PhoneIcon sx={{ fontSize: 36, color: '#dd8709' }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', color: 'gray' }}
              >
                Contact Number
              </Typography>
              <Link
                href="tel:+447464473560"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  textDecoration: 'none',
                  '&:hover': { color: '#dd8709' },
                }}
              >
                +44 7464 473560
              </Link>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={infoCardSx}>
              <EmailIcon sx={{ fontSize: 36, color: '#dd8709' }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', color: 'gray' }}
              >
                Email Address
              </Typography>
              <Link
                href="mailto:support@barbers.limited"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  textDecoration: 'none',
                  '&:hover': { color: '#dd8709' },
                }}
              >
                support@barbers.limited
              </Link>
            </Box>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={infoCardSx}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 36, color: '#dd8709' }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', color: 'gray' }}
              >
                Chat
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.85)' }}
              >
                Chat with our support team, available Mon–Fri, 9am–5pm.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* FAQs */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 'bold', mb: 3, color: '#dd8709' }}
        >
          Frequently Asked Questions
        </Typography>
        <Box sx={{ mb: 2 }}>
          {faqs.map((faq, index) => (
            <Accordion
              key={faq.question}
              sx={{
                bgcolor: '#16171c',
                color: '#fff',
                mb: 1.5,
                border: '1px solid rgba(255,255,255,0.1)',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: '#dd8709' }} />}
                aria-controls={`faq-${index}-content`}
                id={`faq-${index}-header`}
              >
                <Typography sx={{ fontWeight: 'bold' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body1"
                  sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8 }}
                >
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>

      <StickyFooter {...companyName} />
    </div>
  );
}
